import { Grid, Typography } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../session/InternalAuthProvider';
import { AccountData, SchedulerConfigurationData } from '../../api';
import { useLanguage } from '../../common/GeneralUtils';
import { getOrderPage } from '../../utils/AccountUtils';
import { GeneratedResource } from './ResourceColumn';
import { DEFAULT_ORDER_PAGE_COLOR } from '../../common/theme/mbTokens';

export const ResourceHeaders = () => {
  const { t } = useTranslation();
  const { authState } = useAuth();
  const account = authState.account as unknown as AccountData;
  const language = useLanguage();

  // Generate resources from scheduler configs
  const generateResources = (): GeneratedResource[] => {
    const resources: GeneratedResource[] = [];
    const schedulerConfigs = account?.shared_resources?.scheduler_configs || [];

    schedulerConfigs.forEach((config: SchedulerConfigurationData) => {
      // Get workshop color from the first allowed mask (order page)
      const primaryOrderPageId = config.allowed_masks?.[0]?.order_page_id;
      const orderPage = primaryOrderPageId ? getOrderPage(account, primaryOrderPageId) : null;
      const workshopColor = orderPage?.color || DEFAULT_ORDER_PAGE_COLOR;

      for (let i = 1; i <= config.resource_count; i++) {
        const resourceLabel = config.resource_labels?.[i - 1];
        const labelName =
          resourceLabel?.[language as keyof typeof resourceLabel] ||
          resourceLabel?.en ||
          `${(config.label as any)[language] || (config.label as any).en || 'Resource'} ${i}`;

        resources.push({
          id: `${config.id}-${i}`,
          name: labelName,
          index: i,
          schedulerConfigId: config.id,
          workshopColor,
        });
      }
    });

    return resources;
  };

  const resources = generateResources();
  const totalColumns = resources.length + 1; // +1 for unassigned column
  const columnWidth = 100 / totalColumns;

  return (
    <Grid
      container
      sx={{
        height: '30px',
        borderBottom: '1px solid',
        borderColor: 'mb.lineSoft',
        backgroundColor: 'mb.ground',
        width: '100%',
      }}>
      {resources.map((resource) => (
        <Grid
          key={resource.id}
          item
          sx={{
            width: `${columnWidth}%`,
            borderInlineEnd: '1px solid',
            borderInlineEndColor: 'mb.lineSoft',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '4px',
            backgroundColor: resource.workshopColor
              ? `${resource.workshopColor}66` // 40% opacity (66 in hex = 102/255 ≈ 0.4)
              : 'mb.ground',
          }}>
          <Typography variant="caption" fontWeight="bold">
            {resource.name}
          </Typography>
        </Grid>
      ))}
      {/* Unassigned Header */}
      <Grid
        item
        sx={{
          width: `${columnWidth}%`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '4px',
        }}>
        <Typography variant="caption" fontWeight="bold">
          {t('resources.unassigned', 'Unassigned')}
        </Typography>
      </Grid>
    </Grid>
  );
};
